/**
 * SelectionController - Unified selection state management
 * 
 * Handles all selection logic:
 * - Device selection (single and multi)
 * - Connection auto-selection (for selected device pairs)
 * - Box/marquee selection
 * - Library multi-select (templates)
 * - Port multi-select (for batch cabling)
 */

import { rectsIntersect } from '../utils/geometry.js';

// ============= STATE =============

// Unified selection state - single source of truth for device/connection selection
const selectionState = {
    devices: new Set(),      // Set of device IDs (0 to N)
    connections: new Set()   // Set of connection IDs (0 to N)
};

// Library multi-select state
const libraryMultiSelect = {
    selectedItems: new Set(),    // Set of template IDs
    lastClickedId: null          // For shift-click range selection
};

// Multi-select state for batch port patching
const multiSelectState = {
    selectedPorts: [],  // Array of { deviceId, groupIndex, type, index }
    rangeStart: null,   // For shift+click range selection
    groupIndex: null    // All selected ports must be in same group
};

// Legacy compatibility layer (will be removed after full migration)
const boxSelectState = {
    get selectedDevices() { return Array.from(selectionState.devices); },
    set selectedDevices(arr) {
        selectionState.devices.clear();
        arr.forEach(id => selectionState.devices.add(id));
    },
    get selectedConnections() { return Array.from(selectionState.connections); },
    set selectedConnections(arr) {
        selectionState.connections.clear();
        arr.forEach(id => selectionState.connections.add(id));
    },
    isActive: false
};

// Box selection drag state
let boxDragState = null;

// Module dependencies (injected via init)
let instanceModel = null;
let connectionModel = null;
let definitionModel = null;
let canvasEl = null;
let screenToCanvasFunc = null;
let getPortPositionFunc = null;
let getConnectionSourceGroupFunc = null;
let getConnectionTargetGroupFunc = null;
let cableIntersectsBoxGeomFunc = null;
let debugConfig = null;
let state = null;
let renderLibrary = null;

// ============= INIT =============

/**
 * Initialize the selection controller with dependencies.
 * 
 * Manages all selection state:
 * - Device selection (single/multi via click, box, or programmatic)
 * - Connection auto-selection when devices are selected
 * - Library template multi-select
 * - Port multi-select for batch cabling
 * 
 * @param {Object} deps - Dependencies
 * @param {Object} deps.instanceModel - Device instance model
 * @param {Object} deps.connectionModel - Connection model
 * @param {Object} deps.definitionModel - Template definition model
 * @param {HTMLElement} deps.canvas - Canvas element for box selection overlay
 * @param {Function} deps.screenToCanvas - Convert screen coords to canvas coords
 * @param {Function} deps.getPortPosition - Get port position on canvas
 * @param {Function} deps.getConnectionSourceGroup - Get source group for connection
 * @param {Function} deps.getConnectionTargetGroup - Get target group for connection
 * @param {Function} deps.cableIntersectsBoxGeom - Check if cable intersects selection box
 * @param {Object} deps.debugConfig - Debug configuration
 * @param {Object} deps.state - App state reference
 * @param {Function} deps.renderLibrary - Render library function
 */
export function initSelectionController(deps) {
    instanceModel = deps.instanceModel;
    connectionModel = deps.connectionModel;
    definitionModel = deps.definitionModel;
    canvasEl = deps.canvas;
    screenToCanvasFunc = deps.screenToCanvas;
    getPortPositionFunc = deps.getPortPosition;
    getConnectionSourceGroupFunc = deps.getConnectionSourceGroup;
    getConnectionTargetGroupFunc = deps.getConnectionTargetGroup;
    cableIntersectsBoxGeomFunc = deps.cableIntersectsBoxGeom;
    debugConfig = deps.debugConfig;
    state = deps.state;
    renderLibrary = deps.renderLibrary;
}

// ============= DEVICE SELECTION =============

/**
 * Get the set of selected device IDs
 */
export function getSelectedDevices() {
    return selectionState.devices;
}

/**
 * Get the set of selected connection IDs
 */
export function getSelectedConnections() {
    return selectionState.connections;
}

/**
 * Get the single selected device ID, or null if not exactly one selected
 */
export function getSingleSelectedDevice() {
    return selectionState.devices.size === 1
        ? selectionState.devices.values().next().value
        : null;
}

/**
 * Set device selection to a specific set of device IDs
 * @param {string|string[]} deviceIds - Device ID or array of IDs
 */
export function setDeviceSelection(deviceIds) {
    selectionState.devices.clear();
    if (Array.isArray(deviceIds)) {
        deviceIds.forEach(id => selectionState.devices.add(id));
    } else if (deviceIds) {
        selectionState.devices.add(deviceIds);
    }
    updateConnectionSelection();
    updateSelectionVisuals();
}

/**
 * Add a device to the current selection
 * @param {string} deviceId - Device ID to add
 */
export function addToDeviceSelection(deviceId) {
    selectionState.devices.add(deviceId);
    updateConnectionSelection();
    updateSelectionVisuals();
}

/**
 * Toggle a device in/out of the selection
 * @param {string} deviceId - Device ID to toggle
 */
export function toggleDeviceSelection(deviceId) {
    if (selectionState.devices.has(deviceId)) {
        selectionState.devices.delete(deviceId);
    } else {
        selectionState.devices.add(deviceId);
    }
    updateConnectionSelection();
    updateSelectionVisuals();
}

/**
 * Clear all device and connection selection
 */
export function clearDeviceSelection() {
    selectionState.devices.clear();
    selectionState.connections.clear();
    updateSelectionVisuals();
}

/**
 * Select all devices on the canvas
 */
export function selectAllDevices() {
    instanceModel.forEach(d => selectionState.devices.add(d.id));
    updateConnectionSelection();
    updateSelectionVisuals();
}

/**
 * Check if any devices are selected
 */
export function hasDeviceSelection() {
    return selectionState.devices.size > 0;
}

/**
 * Check if multiple devices are selected
 */
export function hasMultipleDevicesSelected() {
    return selectionState.devices.size > 1;
}

// ============= CONNECTION SELECTION =============

/**
 * Auto-select connections between selected devices
 * Called automatically when device selection changes
 */
function updateConnectionSelection() {
    selectionState.connections.clear();
    if (selectionState.devices.size < 2) return;

    connectionModel.forEach(conn => {
        if (selectionState.devices.has(conn.sourceDeviceId) &&
            selectionState.devices.has(conn.targetDeviceId)) {
            selectionState.connections.add(conn.id);
        }
    });
}

// ============= SELECTION VISUALS =============

/**
 * Update visual state of selected elements (adds/removes CSS classes)
 */
export function updateSelectionVisuals() {
    // Remove all selection classes
    document.querySelectorAll('.device.selected').forEach(d => d.classList.remove('selected'));
    document.querySelectorAll('#cables-svg path.selected').forEach(p => p.classList.remove('selected'));
    document.querySelectorAll('.device-internal-cables path.selected').forEach(p => p.classList.remove('selected'));

    // Apply selected class to all selected devices
    selectionState.devices.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.add('selected');
    });

    // Apply selected class to auto-selected connections (check both global and internal SVGs)
    selectionState.connections.forEach(id => {
        const pathEl = document.querySelector(`#cables-svg path[data-id="${id}"]`) ||
                       document.querySelector(`.device-internal-cables path[data-id="${id}"]`);
        if (pathEl) pathEl.classList.add('selected');
    });

    updateSelectionIndicator();
}

/**
 * Update the selection indicator in the footer
 */
export function updateSelectionIndicator() {
    const indicator = document.getElementById('box-select-indicator');
    const countEl = document.getElementById('box-select-count');

    const devCount = selectionState.devices.size;
    const connCount = selectionState.connections.size;
    const totalCount = devCount + connCount;

    if (totalCount > 1 || (devCount === 1 && connCount > 0)) {
        indicator.classList.add('active');
        const parts = [];
        if (devCount > 0) parts.push(`${devCount} device${devCount > 1 ? 's' : ''}`);
        if (connCount > 0) parts.push(`${connCount} cable${connCount > 1 ? 's' : ''}`);
        countEl.textContent = parts.join(', ');
    } else {
        indicator.classList.remove('active');
    }
}

// ============= SELECTION BOUNDS =============

/**
 * Get bounds of a single device (using DOM element for accurate dimensions)
 */
export function getDeviceBoundsRect(device) {
    const el = document.getElementById(device.id);
    if (!el) return null;
    return {
        left: device.position.x,
        top: device.position.y,
        right: device.position.x + el.offsetWidth,
        bottom: device.position.y + el.offsetHeight
    };
}

/**
 * Compute bounding rectangle of all currently selected devices
 */
export function getSelectionBoundsRect() {
    if (selectionState.devices.size === 0) return null;

    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    selectionState.devices.forEach(deviceId => {
        const device = instanceModel.getById(deviceId);
        if (!device) return;
        const bounds = getDeviceBoundsRect(device);
        if (!bounds) return;

        minX = Math.min(minX, bounds.left);
        minY = Math.min(minY, bounds.top);
        maxX = Math.max(maxX, bounds.right);
        maxY = Math.max(maxY, bounds.bottom);
    });

    if (minX === Infinity) return null;
    return { left: minX, top: minY, right: maxX, bottom: maxY };
}

/**
 * Select all devices that fall within the given bounding rect
 */
export function selectDevicesInBoundsRect(bounds) {
    const devicesInBounds = [];
    instanceModel.forEach(device => {
        const deviceBounds = getDeviceBoundsRect(device);
        if (!deviceBounds) return;
        // Check if device intersects bounds (using geometry utils)
        if (rectsIntersect(bounds, deviceBounds)) {
            devicesInBounds.push(device.id);
        }
    });
    setDeviceSelection(devicesInBounds);
}

// ============= BOX SELECTION =============

/**
 * Get current box drag state
 */
export function getBoxDragState() {
    return boxDragState;
}

/**
 * Set box drag state (for gesture controller cleanup)
 */
export function setBoxDragState(state) {
    boxDragState = state;
}

/**
 * Start box selection drag
 * @param {Event} e - Pointer event
 * @param {Function} getPointerPos - Function to get pointer position
 */
export function startBoxSelection(e, getPointerPos) {
    const pos = getPointerPos(e);
    const canvasPos = screenToCanvasFunc(pos.clientX, pos.clientY);

    // Check for modifier keys (Ctrl/Cmd or Shift)
    const hasModifier = e.shiftKey || e.ctrlKey || e.metaKey;
    const isShiftOnly = e.shiftKey && !e.ctrlKey && !e.metaKey;

    // If not holding any modifier, clear existing box selection
    if (!hasModifier) {
        clearBoxSelection();
    }

    // Clear library selection when interacting with canvas
    if (libraryMultiSelect.selectedItems.size > 0) {
        clearLibraryMultiSelect();
        renderLibrary();
    }

    // Create selection box element
    const selectionBox = document.createElement('div');
    selectionBox.className = 'selection-box';
    selectionBox.id = 'selection-box';
    selectionBox.style.left = canvasPos.x + 'px';
    selectionBox.style.top = canvasPos.y + 'px';
    selectionBox.style.width = '0px';
    selectionBox.style.height = '0px';
    canvasEl.appendChild(selectionBox);

    boxDragState = {
        startX: canvasPos.x,
        startY: canvasPos.y,
        addToSelection: hasModifier,
        extendBounds: isShiftOnly  // Shift = extend bounding rect mode
    };
}

/**
 * Update box selection during drag
 * @param {Event} e - Pointer event
 * @param {Function} getPointerPos - Function to get pointer position
 */
export function handleBoxSelection(e, getPointerPos) {
    if (!boxDragState) return;

    const pos = getPointerPos(e);
    const canvasPos = screenToCanvasFunc(pos.clientX, pos.clientY);

    const selectionBox = document.getElementById('selection-box');
    if (!selectionBox) return;

    // Calculate box dimensions (handle negative dragging)
    const minX = Math.min(boxDragState.startX, canvasPos.x);
    const minY = Math.min(boxDragState.startY, canvasPos.y);
    const maxX = Math.max(boxDragState.startX, canvasPos.x);
    const maxY = Math.max(boxDragState.startY, canvasPos.y);

    selectionBox.style.left = minX + 'px';
    selectionBox.style.top = minY + 'px';
    selectionBox.style.width = (maxX - minX) + 'px';
    selectionBox.style.height = (maxY - minY) + 'px';
}

/**
 * Finish box selection and select items
 * @param {Event} e - Pointer event
 * @param {Function} getPointerPos - Function to get pointer position
 * @param {Function} updateSelectionButtons - Callback to update UI buttons
 */
export function finishBoxSelection(e, getPointerPos, updateSelectionButtons) {
    const selectionBox = document.getElementById('selection-box');
    if (!selectionBox || !boxDragState) {
        boxDragState = null;
        return;
    }

    const pos = getPointerPos(e);
    const canvasPos = screenToCanvasFunc(pos.clientX, pos.clientY);

    // Calculate final box bounds
    const minX = Math.min(boxDragState.startX, canvasPos.x);
    const minY = Math.min(boxDragState.startY, canvasPos.y);
    const maxX = Math.max(boxDragState.startX, canvasPos.x);
    const maxY = Math.max(boxDragState.startY, canvasPos.y);

    const boxRect = { left: minX, top: minY, right: maxX, bottom: maxY };

    // Remove selection box element
    selectionBox.remove();

    // Only select if box is larger than a small threshold (to avoid accidental clicks)
    const boxWidth = maxX - minX;
    const boxHeight = maxY - minY;
    if (boxWidth < 5 && boxHeight < 5) {
        boxDragState = null;
        return;
    }

    // Find all devices that intersect the box
    const devicesInBox = [];
    instanceModel.forEach(device => {
        if (deviceIntersectsBox(device, boxRect)) {
            devicesInBox.push(device.id);
        }
    });

    // Find all connections that intersect the box
    const connectionsInBox = [];
    connectionModel.forEach(conn => {
        if (connectionIntersectsBox(conn, boxRect)) {
            connectionsInBox.push(conn.id);
        }
    });

    // Update selection
    if (boxDragState.extendBounds) {
        // Shift = extend bounding rectangle
        const currentBounds = getSelectionBoundsRect();
        if (currentBounds && devicesInBox.length > 0) {
            // Extend current selection bounds to include the drawn rect
            const extendedBounds = {
                left: Math.min(currentBounds.left, boxRect.left),
                top: Math.min(currentBounds.top, boxRect.top),
                right: Math.max(currentBounds.right, boxRect.right),
                bottom: Math.max(currentBounds.bottom, boxRect.bottom)
            };
            // Select all devices within the extended bounding rectangle
            selectDevicesInBoundsRect(extendedBounds);
        } else if (currentBounds) {
            // No devices in drawn rect, extend to rect anyway
            const extendedBounds = {
                left: Math.min(currentBounds.left, boxRect.left),
                top: Math.min(currentBounds.top, boxRect.top),
                right: Math.max(currentBounds.right, boxRect.right),
                bottom: Math.max(currentBounds.bottom, boxRect.bottom)
            };
            selectDevicesInBoundsRect(extendedBounds);
        } else {
            // No existing selection, just select devices in the drawn rect
            setDeviceSelection(devicesInBox);
        }
    } else if (boxDragState.addToSelection) {
        // Ctrl/Cmd = add to existing selection
        devicesInBox.forEach(id => addToDeviceSelection(id));
    } else {
        // No modifier = replace selection
        setDeviceSelection(devicesInBox);
    }

    // Clear other selection types
    state.selectedConnectionId = null;
    state.selectedTemplateId = null;

    // Update UI
    updateSelectionButtons();

    boxDragState = null;
}

/**
 * Check if a device intersects a bounding box
 */
function deviceIntersectsBox(device, boxRect) {
    const el = document.getElementById(device.id);
    if (!el) return false;

    // Get device bounds in canvas coordinates
    const deviceBounds = {
        left: device.position.x,
        top: device.position.y,
        right: device.position.x + el.offsetWidth,
        bottom: device.position.y + el.offsetHeight
    };

    // Check if rectangles intersect (using geometry utils)
    return rectsIntersect(deviceBounds, boxRect);
}

/**
 * Check if a connection (cable) intersects a bounding box
 */
function connectionIntersectsBox(conn, boxRect) {
    // Get source and target positions
    const sourceGroupIndex = getConnectionSourceGroupFunc(conn);
    const targetGroupIndex = getConnectionTargetGroupFunc(conn);
    const sourcePos = getPortPositionFunc(conn.sourceDeviceId, sourceGroupIndex, 'output', conn.sourcePortIndex);
    const targetPos = getPortPositionFunc(conn.targetDeviceId, targetGroupIndex, 'input', conn.targetPortIndex);

    if (!sourcePos || !targetPos) return false;

    // Check if the cable line intersects the box
    const straightDist = debugConfig.cableStraightDist;
    return cableIntersectsBoxGeomFunc(
        sourcePos.x, sourcePos.y, sourcePos.side,
        targetPos.x, targetPos.y, targetPos.side,
        boxRect, straightDist
    );
}

/**
 * Clear box selection
 */
export function clearBoxSelection() {
    clearDeviceSelection();
    // Also clean up the box selection UI if it's active
    boxDragState = null;
    const selectionBox = document.getElementById('selection-box');
    if (selectionBox) selectionBox.remove();
}

/**
 * Check if there's an active box selection
 */
export function hasBoxSelection() {
    return hasDeviceSelection();
}

/**
 * Toggle device in selection (for Ctrl/Shift+click)
 * @param {string} deviceId - Device to toggle
 * @param {boolean} isShift - Whether shift key is held (bounding rect extension mode)
 * @param {Function} updateSelectionButtons - Callback to update UI
 */
export function toggleDeviceInBoxSelection(deviceId, isShift, updateSelectionButtons) {
    // Clear other selection types
    state.selectedConnectionId = null;
    state.selectedTemplateId = null;

    if (isShift) {
        // Shift = extend bounding rectangle to include clicked device
        const clickedDevice = instanceModel.getById(deviceId);
        if (!clickedDevice) return;

        const clickedBounds = getDeviceBoundsRect(clickedDevice);
        if (!clickedBounds) return;

        const currentBounds = getSelectionBoundsRect();

        if (currentBounds) {
            // Extend current selection bounds to include clicked device
            const extendedBounds = {
                left: Math.min(currentBounds.left, clickedBounds.left),
                top: Math.min(currentBounds.top, clickedBounds.top),
                right: Math.max(currentBounds.right, clickedBounds.right),
                bottom: Math.max(currentBounds.bottom, clickedBounds.bottom)
            };
            // Select all devices within extended bounds
            selectDevicesInBoundsRect(extendedBounds);
        } else {
            // No current selection, just select clicked device
            setDeviceSelection(deviceId);
        }
    } else {
        // Ctrl/Cmd = toggle individual device
        toggleDeviceSelection(deviceId);
    }

    updateSelectionButtons();
}

// ============= LIBRARY MULTI-SELECT =============

/**
 * Get the library multi-select state
 */
export function getLibraryMultiSelect() {
    return libraryMultiSelect;
}

/**
 * Clear library multi-selection
 */
export function clearLibraryMultiSelect() {
    libraryMultiSelect.selectedItems.clear();
    libraryMultiSelect.lastClickedId = null;
    state.selectedTemplateId = null;
}

/**
 * Select all library items (for Ctrl+A)
 * @param {string} librarySearchFilter - Current search filter
 * @param {Function} updateSelectionButtons - Callback to update UI
 */
export function selectAllLibraryItems(librarySearchFilter, updateSelectionButtons) {
    const filteredTemplates = librarySearchFilter
        ? definitionModel.search(librarySearchFilter)
        : definitionModel.getAll();

    libraryMultiSelect.selectedItems.clear();
    filteredTemplates.forEach(t => libraryMultiSelect.selectedItems.add(t.id));

    if (filteredTemplates.length > 0 && !state.selectedTemplateId) {
        state.selectedTemplateId = filteredTemplates[0].id;
        libraryMultiSelect.lastClickedId = filteredTemplates[0].id;
    }

    updateSelectionButtons();
    renderLibrary();
}

/**
 * Handle library item click with modifier keys
 * @param {Event} e - Click event
 * @param {string} templateId - Clicked template ID
 * @param {string[]} filteredIds - List of visible template IDs (for range selection)
 * @param {Function} updateSelectionButtons - Callback to update UI
 */
export function handleLibraryItemSelection(e, templateId, filteredIds, updateSelectionButtons) {
    const isCtrl = e.ctrlKey || e.metaKey;
    const isShift = e.shiftKey;

    if (isShift && libraryMultiSelect.lastClickedId) {
        // Shift+click: range selection
        const lastIdx = filteredIds.indexOf(libraryMultiSelect.lastClickedId);
        const currentIdx = filteredIds.indexOf(templateId);

        if (lastIdx !== -1 && currentIdx !== -1) {
            const minIdx = Math.min(lastIdx, currentIdx);
            const maxIdx = Math.max(lastIdx, currentIdx);

            if (!isCtrl) {
                // Clear existing selection first
                libraryMultiSelect.selectedItems.clear();
            }

            // Select all items in range
            for (let i = minIdx; i <= maxIdx; i++) {
                libraryMultiSelect.selectedItems.add(filteredIds[i]);
            }

            // Keep selectedTemplateId in sync
            if (!state.selectedTemplateId || !libraryMultiSelect.selectedItems.has(state.selectedTemplateId)) {
                state.selectedTemplateId = libraryMultiSelect.lastClickedId;
            }
        }
    } else if (isCtrl) {
        // Ctrl+click: toggle selection
        if (libraryMultiSelect.selectedItems.has(templateId)) {
            libraryMultiSelect.selectedItems.delete(templateId);
            // Update selectedTemplateId if we removed the current one
            if (state.selectedTemplateId === templateId) {
                state.selectedTemplateId = libraryMultiSelect.selectedItems.size > 0
                    ? libraryMultiSelect.selectedItems.values().next().value
                    : null;
            }
        } else {
            libraryMultiSelect.selectedItems.add(templateId);
            state.selectedTemplateId = templateId;
        }

        // Update lastClickedId for future shift+clicks
        libraryMultiSelect.lastClickedId = templateId;
    } else {
        // Normal click: select single item
        libraryMultiSelect.selectedItems.clear();
        libraryMultiSelect.selectedItems.add(templateId);
        libraryMultiSelect.lastClickedId = templateId;
        state.selectedTemplateId = templateId;
    }

    // Clear canvas device selection when selecting library items
    clearDeviceSelection();

    updateSelectionButtons();
    renderLibrary();
}

// ============= PORT MULTI-SELECT =============

/**
 * Get the port multi-select state
 */
export function getMultiSelectState() {
    return multiSelectState;
}

/**
 * Handle range selection for ports (Shift+click)
 * @param {Object} portInfo - Port info { deviceId, groupIndex, type, index }
 * @param {HTMLElement} portEl - Port element
 */
export function handleRangeSelect(portInfo, portEl) {
    const { deviceId, groupIndex, type, index } = portInfo;
    const direction = portEl.dataset.direction;

    // If no range start or different group, set this as range start
    if (!multiSelectState.rangeStart || multiSelectState.groupIndex !== groupIndex) {
        clearMultiSelection();
        multiSelectState.rangeStart = portInfo;
        multiSelectState.rangeStart.direction = direction;  // Store direction
        multiSelectState.groupIndex = groupIndex;
        multiSelectState.selectedPorts = [portInfo];
        portEl.classList.add('multi-selected', 'range-start');
        updateMultiSelectIndicator();
        return;
    }

    // Same group - select range between rangeStart and this port
    const startIdx = multiSelectState.rangeStart.index;
    const endIdx = index;
    let minIdx = Math.min(startIdx, endIdx);
    let maxIdx = Math.max(startIdx, endIdx);
    
    // When clicking the same note as the range start, create a single semitone range (start to start+1)
    if (startIdx === endIdx) {
        maxIdx = startIdx + 1;
    }
    
    const rangeDirection = multiSelectState.rangeStart.direction || 'output';

    // Clear current selection visuals
    document.querySelectorAll('.port.multi-selected').forEach(p => {
        p.classList.remove('multi-selected', 'range-start');
    });

    // Select all ports in range (using the direction of the range start)
    multiSelectState.selectedPorts = [];
    for (let i = minIdx; i <= maxIdx; i++) {
        const port = { deviceId, groupIndex, type, index: i };
        multiSelectState.selectedPorts.push(port);

        const pEl = document.querySelector(
            `.port[data-device="${deviceId}"][data-group="${groupIndex}"][data-direction="${rangeDirection}"][data-index="${i}"]`
        );
        if (pEl) {
            pEl.classList.add('multi-selected');
            if (i === startIdx) pEl.classList.add('range-start');
        }
    }

    updateMultiSelectIndicator();
}

/**
 * Handle toggle selection for ports (Ctrl+click)
 * @param {Object} portInfo - Port info { deviceId, groupIndex, type, index }
 * @param {HTMLElement} portEl - Port element
 */
export function handleToggleSelect(portInfo, portEl) {
    const { deviceId, groupIndex, type, index } = portInfo;

    // If selecting from a different group, clear and start fresh
    if (multiSelectState.groupIndex !== null && multiSelectState.groupIndex !== groupIndex) {
        clearMultiSelection();
    }

    multiSelectState.groupIndex = groupIndex;

    const existingIdx = multiSelectState.selectedPorts.findIndex(
        p => p.deviceId === deviceId && p.groupIndex === groupIndex && p.index === index
    );

    if (existingIdx >= 0) {
        // Deselect
        multiSelectState.selectedPorts.splice(existingIdx, 1);
        portEl.classList.remove('multi-selected', 'range-start');

        // Clear range start if this was it
        if (multiSelectState.rangeStart &&
            multiSelectState.rangeStart.deviceId === deviceId &&
            multiSelectState.rangeStart.groupIndex === groupIndex &&
            multiSelectState.rangeStart.index === index) {
            multiSelectState.rangeStart = null;
        }

        // Clear groupIndex if no selections remain
        if (multiSelectState.selectedPorts.length === 0) {
            multiSelectState.groupIndex = null;
        }
    } else {
        // Select
        multiSelectState.selectedPorts.push(portInfo);
        portEl.classList.add('multi-selected');

        // Set as range start for subsequent shift+clicks
        multiSelectState.rangeStart = portInfo;

        // Update range-start visual
        document.querySelectorAll('.port.range-start').forEach(p => {
            p.classList.remove('range-start');
        });
        portEl.classList.add('range-start');
    }

    updateMultiSelectIndicator();
}

/**
 * Check if a port is selected
 * @param {Object} portInfo - Port info to check
 */
export function isPortSelected(portInfo) {
    return multiSelectState.selectedPorts.some(
        p => p.deviceId === portInfo.deviceId &&
             p.groupIndex === portInfo.groupIndex &&
             p.index === portInfo.index
    );
}

/**
 * Clear all port multi-selection
 */
export function clearMultiSelection() {
    document.querySelectorAll('.port.multi-selected').forEach(p => {
        p.classList.remove('multi-selected', 'range-start');
    });
    multiSelectState.selectedPorts = [];
    multiSelectState.rangeStart = null;
    multiSelectState.groupIndex = null;
    updateMultiSelectIndicator();
}

/**
 * Update the multi-select indicator in the UI
 */
function updateMultiSelectIndicator() {
    const indicator = document.getElementById('multi-select-indicator');
    const countEl = document.getElementById('multi-select-count');

    if (multiSelectState.selectedPorts.length > 0) {
        indicator.classList.add('active');
        countEl.textContent = multiSelectState.selectedPorts.length;
    } else {
        indicator.classList.remove('active');
    }
}

// ============= LEGACY COMPATIBILITY =============

/**
 * Get legacy boxSelectState object (for gradual migration)
 */
export function getBoxSelectState() {
    return boxSelectState;
}

/**
 * Legacy function names that delegate to unified selection helpers
 */
export function updateBoxSelectionVisuals() {
    updateSelectionVisuals();
}

export function updateBoxSelectIndicator() {
    updateSelectionIndicator();
}
